/**
 * usePracticeUpload.ts — Extracted from PracticeView.vue Phase 5c
 *
 * Contains practice upload telemetry: pushFocusPoint, upload, uploadPractice.
 * Also exports the FocusPoint and VoiceEvent interfaces.
 * This is a purely mechanical extraction with zero logic changes.
 *
 * State remains in PracticeView's reactive object; this composable operates
 * on Ref-wrapped state properties passed via the deps interface.
 */
import moment from 'moment'
import type { AliyahType, HaftarahType, SentenceGroupType, SentenceType } from 'types/types'
import { ComputedRef, Ref } from 'vue'
import { VueLogger } from 'vue-logger-plugin'

import { ReadOrder } from '@/composables/usePracticeFSM'
import {
  extractContentIds,
  STUDENT_VOICE_EXTRACT_PRE_BUFFER_MS,
  UPLOAD_FAILED_TITLE,
  VOICE_MODE_READING,
  VOICE_MODE_SINGING,
} from '@/constants/practiceConstants'
import UploadPractice from '@/mutations/UploadPractice.graphql'
import { runReportedMutation } from '@/store/mutationHelpers'
import { mimeToExtension, SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import type { RecordingType } from '@/util/Recording'
import { inSingMode, type VoiceStyleSet } from '@/util/VoiceStyles'

// {{{ interfaces
export interface FocusPoint {
  moment: string
  aliyah: number | null
  haftarah: number | null
  sentenceGroup: number | null
  sentence: number | null
  segmentedSentence: number | null
}

export interface SegmentPoint {
  moment: string
  sentence: number | null
  segmentIndex: number
}

export interface VoiceEvent {
  voiceStartAt: number
  voiceStopAt: number
}
// }}}

// {{{ voice-event compressed timeline
/**
 * Compute the current position in the compressed (student-only) audio timeline.
 *
 * The backend strips non-voice periods from the recording using voice events and
 * concatenates only the voice-active segments (with a pre-buffer). The playback
 * overlay serves this compressed file, so focus/segment point timestamps must
 * reference the compressed timeline rather than wall-clock time.
 */
export function compressedAudioTimeMs(voiceEvents: VoiceEvent[], nowRelativeMs?: number): number {
  let compressed = 0
  let currentStart: number | null = null

  for (const event of voiceEvents) {
    if (event.voiceStartAt !== 0) {
      currentStart = Math.max(0, event.voiceStartAt - STUDENT_VOICE_EXTRACT_PRE_BUFFER_MS)
    }
    if (event.voiceStopAt !== 0 && currentStart !== null) {
      compressed += event.voiceStopAt - currentStart
      currentStart = null
    }
  }

  // Currently in a voice-active period: add elapsed time since voice start
  if (currentStart !== null && nowRelativeMs !== undefined) {
    compressed += Math.max(0, nowRelativeMs - currentStart)
  }

  return compressed
}

/** Total compressed duration of all completed voice segments (ignores any open segment). */
export function totalCompressedDuration(voiceEvents: VoiceEvent[]): number {
  return compressedAudioTimeMs(voiceEvents)
}

/**
 * Discard trailing focus/segment points that fall at or past the total compressed audio
 * duration — these represent sentences the student advanced to but never actually sang.
 */
export function trimTrailingInactivePoints(
  focusPoints: FocusPoint[],
  segmentPoints: SegmentPoint[],
  voiceEvents: VoiceEvent[],
): { focusPoints: FocusPoint[]; segmentPoints: SegmentPoint[] } {
  if (!voiceEvents.length || !focusPoints.length) {
    return { focusPoints, segmentPoints }
  }
  // If the last voice event is an open voiceStart (student was singing when recording stopped),
  // don't trim — the recording ended mid-voice, so there are no trailing inactive points
  const lastEvent = voiceEvents[voiceEvents.length - 1]
  if (lastEvent.voiceStartAt !== 0 && lastEvent.voiceStopAt === 0) {
    return { focusPoints, segmentPoints }
  }
  const duration = totalCompressedDuration(voiceEvents)
  if (duration === 0) {
    return { focusPoints, segmentPoints }
  }
  return {
    focusPoints: focusPoints.filter((fp) => Number(fp.moment) < duration),
    segmentPoints: segmentPoints.filter((sp) => Number(sp.moment) < duration),
  }
}
// }}}

// {{{ interface
export interface PracticeUploadDeps {
  // State refs (from PracticeView's toRefs)
  aliyah: Ref<AliyahType | null>
  focusPoints: Ref<Array<FocusPoint>>
  focusSentenceIndex: Ref<number | null>
  focusSentenceRecordingMetadataWithPitchedAudios: Ref<SentenceRecordingMetadataWithPitchedAudios | null>
  haftarah: Ref<HaftarahType | null>
  readOrder: Ref<ReadOrder>
  recordingStartedAt: Ref<number>
  segmentPoints: Ref<Array<SegmentPoint>>
  sentenceGroup: Ref<SentenceGroupType | null>
  uploadRecordingOverlay: Ref<boolean>
  uploading: Ref<boolean>
  voiceEvents: Ref<Array<VoiceEvent>>
  voiceStyleSet: Ref<VoiceStyleSet>

  // Computed refs
  sentence: ComputedRef<SentenceType | null>

  // Store
  store: {
    dispatch: {
      dialog: { show: (payload: { message: string; title: string }) => void }
      snackbar: { add: (payload: { message: string; state: string }) => void }
    }
  }
}
// }}}

export function usePracticeUpload(logger: VueLogger, deps: PracticeUploadDeps) {
  // {{{ currentMomentMs
  /**
   * Compute the current timestamp for a focus/segment point.
   * Always returns compressed-audio time (matching the student-only file the
   * backend creates from voice events). When no voice events have fired yet,
   * compressed time is 0 — which is correct because no voiced audio exists yet
   * in the compressed file, and the next voiced segment will start near 0ms.
   */
  function currentMomentMs(): string {
    const recordingStart = deps.recordingStartedAt.value
    if (!recordingStart) {
      return String(0)
    }
    const nowRelative = Date.now() - recordingStart
    const compressed = compressedAudioTimeMs(deps.voiceEvents.value, nowRelative)
    console.log(`[pushPoint] nowRelative=${nowRelative} voiceEvents=${deps.voiceEvents.value.length} compressed=${compressed}`)
    return String(compressed)
  }
  // }}}

  // {{{ pushFocusPoint
  /**
   * @postcondition New FocusPoint appended to deps.focusPoints with current timestamp and content IDs
   * @sideeffect Mutates deps.focusPoints array
   */
  function pushFocusPoint() {
    const ids = extractContentIds(deps.aliyah.value, deps.haftarah.value, deps.sentence.value, deps.sentenceGroup.value)
    const segmentedSentenceId = deps.focusSentenceRecordingMetadataWithPitchedAudios.value?.sentenceRecording?.segmentedSentence?.id ?? null
    deps.focusPoints.value.push({
      aliyah: ids.aliyahId,
      haftarah: ids.haftarahId,
      moment: currentMomentMs(),
      segmentedSentence: segmentedSentenceId ? Number(segmentedSentenceId) : null,
      sentence: ids.sentenceId,
      sentenceGroup: ids.sentenceGroupId,
    })
  }
  // }}}
  // {{{ pushSegmentPoint
  function pushSegmentPoint(segmentIndex: number) {
    const ids = extractContentIds(deps.aliyah.value, deps.haftarah.value, deps.sentence.value, deps.sentenceGroup.value)
    deps.segmentPoints.value.push({
      moment: currentMomentMs(),
      segmentIndex,
      sentence: ids.sentenceId,
    })
  }
  // }}}
  // {{{ upload
  /**
   * @postcondition File uploaded via UploadPractice GraphQL mutation with focus points and voice events
   * @sideeffect Sets uploading=true; sends UploadPractice; shows error dialog on failure
   */
  function upload(file: File, shared: boolean): void {
    deps.uploading.value = true
    const ids = extractContentIds(deps.aliyah.value, deps.haftarah.value, deps.sentence.value, deps.sentenceGroup.value)
    const vss = deps.voiceStyleSet.value
    const trimmed = trimTrailingInactivePoints(deps.focusPoints.value, deps.segmentPoints.value, deps.voiceEvents.value)
    console.log(`[upload] voiceEvents count: ${deps.voiceEvents.value.length}`, deps.voiceEvents.value)
    console.log(`[upload] focusPoints count: ${trimmed.focusPoints.length}`, trimmed.focusPoints)
    console.log(`[upload] readOrder: ${ReadOrder[deps.readOrder.value]}`)
    runReportedMutation(
      UploadPractice,
      {
        aliyah: ids.aliyahId,
        file,
        focusPoints: trimmed.focusPoints,
        haftarah: ids.haftarahId,
        haftarahMelody: vss.haftarahMelody?.id ?? null,
        pronunciation: vss.pronunciation?.id ?? null,
        readOrder: ReadOrder[deps.readOrder.value],
        segmentPoints: trimmed.segmentPoints,
        sentence: ids.sentenceId,
        sentenceGroup: ids.sentenceGroupId,
        sentenceGroupMelody: vss.sentenceGroupMelody?.id ?? null,
        shared,
        torahMelody: vss.torahMelody?.id ?? null,
        voice: inSingMode(vss) ? VOICE_MODE_SINGING : VOICE_MODE_READING,
        voiceEvents: deps.voiceEvents.value,
      },
      'uploadPractice',
      (message) => deps.store.dispatch.dialog.show({ message, title: UPLOAD_FAILED_TITLE }),
    )
      .then((uploadData) => {
        if (uploadData) {
          const msg = shared ? 'Recording saved and shared with teacher' : 'Recording saved'
          deps.store.dispatch.snackbar.add({ message: msg, state: 'primary' })
        }
      })
      .finally(() => {
        deps.uploading.value = false
        deps.uploadRecordingOverlay.value = false
      })
  }
  // }}}
  // {{{ uploadPractice
  /**
   * @postcondition Recording blob converted to File with descriptive filename and uploaded
   * @sideeffect Creates File from recording blob; calls upload()
   */
  function uploadPractice(recording: RecordingType, shared: boolean): void {
    if (!deps.sentence.value || deps.focusSentenceIndex.value === null) {
      logger.debug('null focusSentence or focusSentenceIndex in uploadPractice; skipping')
      return
    }

    const blob = new Blob([recording.blob], { type: recording.mimeType })
    const extension = mimeToExtension(blob.type)

    let fileName = ''
    if (deps.aliyah.value) {
      fileName += `Par-${deps.aliyah.value.parashah.transliteratedTitle}-Ali-${deps.aliyah.value.rank}`
    } else if (deps.haftarah.value) {
      fileName += `Haf-${deps.haftarah.value.parashah.transliteratedTitle}`
    } else if (deps.sentenceGroup.value) {
      fileName += `Txt-${deps.sentenceGroup.value.reading.description}`
    }
    fileName += `-Sen-${deps.focusSentenceIndex.value + 1}`
    fileName += `-Pron-${deps.voiceStyleSet.value.pronunciation?.description}`
    fileName += `-${moment().format('YYYY-MM-DD-hh-mm-ss')}`
    fileName += `.${extension}`
    const file = new File([blob], fileName, { type: recording.mimeType })
    upload(file, shared)
  }
  // }}}

  return {
    pushFocusPoint,
    pushSegmentPoint,
    upload,
    uploadPractice,
  }
}
